import { FDRStatus, FDRStatusEntry, FieldModel, UserId } from '@cibo/core'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import { Box, Chip, Stack, StackProps } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ProgramProgressChip } from '..'
import { StatusChip } from './StatusChip'

export type FieldStatusChipProps = {
  field: FieldModel
  // @TODO: simplify to just engagementId after CPD-4124
  onClickEngaged?: ({
    engagementId,
    programId,
    userId,
  }: {
    engagementId: string
    programId: string
    userId: UserId
  }) => void
  onClickEnrolling?: ({ workflowId }: { workflowId: string }) => void
  onClickNeedsInfo?: (field: FieldModel) => void
  onClickProgram?: (programId: string) => void
  outerStackProps?: StackProps
}

export const FieldStatusChips = ({
  field,
  onClickEnrolling,
  onClickEngaged,
  onClickProgram,
  onClickNeedsInfo,
  outerStackProps,
}: FieldStatusChipProps) => {
  const { t } = useTranslation('@cibo/programs/components/FieldStatusChips')
  if (!field.isPartnerField && !field.fdrGlobalStatus.includes(FDRStatus.remoteSensingResolved)) {
    return null
  }

  const enrolled = field.fdrStatus.filter(({ status }: FDRStatusEntry) =>
    status.includes(FDRStatus.enrolled)
  )
  const enrolling = field.fdrStatus.filter(
    ({ status }: FDRStatusEntry) =>
      status.includes(FDRStatus.enrolling) && !status.includes(FDRStatus.isEngaged)
  )
  const engaged = field.fdrStatus.filter(
    ({ status }: FDRStatusEntry) =>
      status.includes(FDRStatus.isEngaged) && !status.includes(FDRStatus.enrolled)
  )
  const needsInfo = field.fdrGlobalStatus.includes(FDRStatus.needsInfo)
  const prequalified = field.fdrGlobalStatus.includes(FDRStatus.eligibleForEnrollment)
  const disqualified = field.fdrStatus.every(({ status }: FDRStatusEntry) =>
    status.includes(FDRStatus.ineligibleForEnrollment)
  )

  const future = field.fdrStatus.some(
    ({ status }: FDRStatusEntry) =>
      status.includes(FDRStatus.futureEnrollment) && status.includes(FDRStatus.hasOpportunity)
  )

  return (
    <Stack
      justifySelf="center"
      spacing={1}
      flex={1}
      py={0.5}
      overflow="hidden"
      {...outerStackProps}
    >
      {engaged.map((fdrStatusEntry: FDRStatusEntry) => {
        const engagementId = field.workflowForProgram(fdrStatusEntry.programId)
        return (
          <Box key={fdrStatusEntry.programId}>
            <StatusChip
              // @todo support other tokens!
              dollarPotential={field.programTokenPotential({
                programId: fdrStatusEntry.programId,
                token: 'usd',
              })}
              size="small"
              onClick={
                !!onClickEngaged && !!engagementId
                  ? () =>
                      onClickEngaged({
                        engagementId,
                        programId: fdrStatusEntry.programId,
                        userId: field.ownerId,
                      })
                  : undefined
              }
              clickable={!!onClickEnrolling}
              fdrStatusEntry={fdrStatusEntry}
              workflowId={engagementId}
              programId={fdrStatusEntry.programId}
              fieldModel={field}
            />
          </Box>
        )
      })}

      {enrolling.map((fdrStatusEntry: FDRStatusEntry) => {
        const workflowId = field.workflowForProgram(fdrStatusEntry.programId)
        return (
          <Box key={fdrStatusEntry.programId}>
            <StatusChip
              // @todo support other tokens!
              dollarPotential={field.programTokenPotential({
                programId: fdrStatusEntry.programId,
                token: 'usd',
              })}
              size="small"
              onClick={
                !!workflowId && !!onClickEnrolling
                  ? () =>
                      onClickEnrolling({
                        workflowId,
                      })
                  : undefined
              }
              clickable={!!onClickEnrolling}
              fdrStatusEntry={fdrStatusEntry}
              workflowId={workflowId}
              programId={fdrStatusEntry.programId}
              fieldModel={field}
            />
          </Box>
        )
      })}

      {enrolled.map((fdrStatusEntry: FDRStatusEntry) => {
        const workflowId = field.workflowForProgram(fdrStatusEntry.programId)
        return (
          <Box key={fdrStatusEntry.programId}>
            <StatusChip
              // @todo support other tokens!
              dollarPotential={field.programTokenPotential({
                programId: fdrStatusEntry.programId,
                token: 'usd',
              })}
              size="small"
              onClick={
                !!onClickProgram ? () => onClickProgram(fdrStatusEntry.programId) : undefined
              }
              clickable={!!onClickProgram}
              fdrStatusEntry={fdrStatusEntry}
              workflowId={workflowId}
              programId={fdrStatusEntry.programId}
              fieldModel={field}
            />
          </Box>
        )
      })}

      {!!future && (
        <Box>
          {/* @ts-ignore color is an extended theme prop */}
          <Chip icon={<AccessTimeRoundedIcon />} color="future" label={t('future')} size="small" />
        </Box>
      )}

      {!!needsInfo && !!onClickNeedsInfo && (
        <Box>
          <ProgramProgressChip
            onClick={() => onClickNeedsInfo(field)}
            progress="needs_info"
            size="small"
          />
        </Box>
      )}
      {!needsInfo && !enrolling.length && !enrolled.length && !!prequalified && (
        <Box>
          <ProgramProgressChip progress="prequalified" size="small" />
        </Box>
      )}
      {!!disqualified && (
        <Box>
          <ProgramProgressChip progress="disqualified" size="small" />
        </Box>
      )}
    </Stack>
  )
}
